import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, AlertCircle } from 'lucide-react';

interface SnackbarProps {
    message: string | null;
    type: 'success' | 'error' | null;
    onClose: () => void;
    duration?: number;
}

const Snackbar: React.FC<SnackbarProps> = ({ message, type, onClose, duration = 5000 }) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (message) {
            setVisible(true);
            const timer = setTimeout(() => {
                setVisible(false);
                setTimeout(onClose, 350); // wait for exit animation
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [message, duration, onClose]);

    if (!message) return null;

    const displayMessage = typeof message === 'string'
        ? message
        : (typeof message === 'object' ? JSON.stringify(message) : String(message));

    const isSuccess = type === 'success';

    return createPortal(
        <div
            id="snackbar"
            style={{
                position: 'fixed',
                top: '24px',
                right: '24px',
                zIndex: 999999,
                minWidth: '320px',
                maxWidth: '440px',
                padding: '16px 20px',
                borderRadius: '14px',
                backgroundColor: isSuccess ? '#059669' : '#dc2626',
                boxShadow: isSuccess
                    ? '0 20px 40px rgba(5, 150, 105, 0.35), 0 4px 12px rgba(0,0,0,0.15)'
                    : '0 20px 40px rgba(220, 38, 38, 0.35), 0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                transform: visible ? 'translateX(0) scale(1)' : 'translateX(120%) scale(0.9)',
                opacity: visible ? 1 : 0,
                transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.4s ease',
                pointerEvents: visible ? 'auto' : 'none',
            }}
        >
            {/* Icon */}
            <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}>
                {isSuccess
                    ? <CheckCircle2 size={22} color="white" />
                    : <AlertCircle size={22} color="white" />
                }
            </div>

            {/* Text */}
            <div style={{ flex: 1 }}>
                <p style={{
                    margin: 0,
                    fontSize: '13px',
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.75)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    lineHeight: '1'
                }}>
                    {isSuccess ? 'Success' : 'Error'}
                </p>
                <p style={{
                    margin: '4px 0 0',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'white',
                    lineHeight: '1.4',
                    wordBreak: 'break-word',
                }}>
                    {displayMessage}
                </p>
            </div>

            {/* Close button */}
            <button
                onClick={() => {
                    setVisible(false);
                    setTimeout(onClose, 350);
                }}
                style={{
                    padding: '6px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'background-color 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)')}
            >
                <X size={16} color="white" />
            </button>
        </div>,
        document.body
    );
};

export default Snackbar;
